import type { Order } from '@cracked-mug/shared';
import { formatOrderNumber, formatPrice, ORDER_STATUS_LABELS } from '@cracked-mug/shared';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';

interface OrderCardProps {
  order: Order;
}

const STATUS_COLORS: Record<Order['status'], string> = {
  pending: 'bg-wca-gold/20 text-wca-gold',
  accepted: 'bg-wca-gold/20 text-wca-gold',
  preparing: 'bg-wca-gold/20 text-wca-gold',
  ready: 'bg-wca-red text-white',
  completed: 'bg-wca-gray-light text-wca-gray-mid',
  cancelled: 'bg-wca-gray-light text-wca-gray-mid line-through',
};

export function OrderCard({ order }: OrderCardProps) {
  const date = new Date(order.placedAt);
  const dateStr = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const itemCount = order.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <Link
      to={`/orders/${order.orderId}`}
      className="block bg-wca-surface rounded-card shadow-card p-4 hover:shadow-lg transition-shadow"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="font-display text-lg">{formatOrderNumber(order.orderNumber)}</p>
            <span
              className={cn(
                'inline-block rounded-full px-2 py-0.5 text-xs font-mono uppercase tracking-wider',
                STATUS_COLORS[order.status],
              )}
            >
              {ORDER_STATUS_LABELS[order.status]}
            </span>
          </div>
          <p className="text-xs text-wca-gray-mid mt-1">{dateStr}</p>
          <p className="text-sm text-wca-gray-mid mt-1 truncate">
            {itemCount} item{itemCount === 1 ? '' : 's'}
            {order.items[0] ? ` · ${order.items[0].name}` : ''}
            {order.items.length > 1 ? ` +${order.items.length - 1} more` : ''}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-mono text-sm text-wca-red tabular-nums">
            {formatPrice(order.totalCents)}
          </p>
          <ChevronRight size={16} className="text-wca-gray-mid mt-1 ml-auto" />
        </div>
      </div>
    </Link>
  );
}
